import {
  Group,
  Text,
  Stack,
  Box,
  Card,
  Select,
  Table,
  Badge,
  Tooltip,
  ScrollArea,
} from '@mantine/core';
import {
  FileText,
  Check,
  Plus,
  Trash2,
} from 'react-feather';
import type { CSVColumnMapping } from '../../../../types';
import { STANDARD_FIELDS } from './importConstants';
import type { FieldType, MappingSummary } from './importTypes';
import classes from '../ImportParticipantsModal.module.css';

interface MapColumnsStepProps {
  file: File | null;
  csvDataRowCount: number;
  csvHeaderCount: number;
  visibleMappings: CSVColumnMapping[];
  includedCustomColumns: Set<string>;
  mappingSummary: MappingSummary;
  onUpdateMapping: (csvColumn: string, targetField: string | null) => void;
  onToggleCustom: (csvColumn: string) => void;
  onIgnoreColumn: (csvColumn: string) => void;
  getSampleValues: (csvColumn: string) => string;
  getFieldType: (mapping: CSVColumnMapping) => FieldType;
  onChangeFile: () => void;
}

export function MapColumnsStep({
  file,
  csvDataRowCount,
  csvHeaderCount,
  visibleMappings,
  includedCustomColumns,
  mappingSummary,
  onUpdateMapping,
  onToggleCustom,
  onIgnoreColumn,
  getSampleValues,
  getFieldType,
  onChangeFile,
}: MapColumnsStepProps) {
  return (
    <Stack gap="lg">
      {/* File Uploaded Banner */}
      {file && (
        <Card className={classes.fileUploadedBanner} p="lg">
          <Group justify="space-between" align="center">
            <Group gap="md">
              <Box
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  border: '2px solid #4A7C59',
                  background: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Check size={18} color="#4A7C59" strokeWidth={2.5} />
              </Box>
              <Box>
                <Group gap="sm" align="center">
                  <Text size="sm" fw={500}>File uploaded:</Text>
                  <Badge
                    variant="default"
                    size="lg"
                    radius="sm"
                    leftSection={<FileText size={14} />}
                    styles={{
                      root: {
                        backgroundColor: 'white',
                        border: '1px solid #E5E7EB',
                        color: '#1F2937',
                        fontWeight: 400,
                        textTransform: 'none',
                        padding: '6px 14px',
                        height: 'auto',
                      },
                    }}
                  >
                    {file.name}
                  </Badge>
                </Group>
                <Text size="sm" c="dimmed" mt={4}>
                  Found {csvDataRowCount} rows and {csvHeaderCount} columns
                </Text>
              </Box>
            </Group>
            <Text
              size="sm"
              className={classes.changeFileLink}
              onClick={onChangeFile}
            >
              Change file
            </Text>
          </Group>
        </Card>
      )}

      {/* Column Mapping Table */}
      <Text fw={600}>Map CSV columns to fields</Text>

      <Card withBorder p={0} radius="md" style={{ overflow: 'hidden' }}>
        <ScrollArea.Autosize mah={400}>
          <Table verticalSpacing={0} horizontalSpacing={0}>
            <Table.Thead>
              <Table.Tr style={{ backgroundColor: '#F9FAFB' }}>
                <Table.Th style={{ width: '35%', padding: '12px 16px' }}><Text size="xs" c="dimmed" tt="uppercase" fw={600}>CSV Column</Text></Table.Th>
                <Table.Th style={{ width: '30%', padding: '12px 16px' }}><Text size="xs" c="dimmed" tt="uppercase" fw={600}>Maps To</Text></Table.Th>
                <Table.Th style={{ width: '20%', padding: '12px 16px' }}><Text size="xs" c="dimmed" tt="uppercase" fw={600}>Type</Text></Table.Th>
                <Table.Th style={{ width: '15%', padding: '12px 16px' }}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {visibleMappings.map((mapping) => {
                const fieldType = getFieldType(mapping);
                const isCustom = !mapping.targetField;
                const isIncluded = isCustom && includedCustomColumns.has(mapping.csvColumn);
                const sampleValues = getSampleValues(mapping.csvColumn);

                return (
                  <Table.Tr key={mapping.csvColumn}>
                    {/* CSV Column with Status Icon */}
                    <Table.Td style={{ padding: '16px', verticalAlign: 'middle' }}>
                      <Group gap="md" wrap="nowrap">
                        {!mapping.targetField ? (
                          // Unmapped - show plus icon for potential custom field inclusion
                          <Tooltip label={isIncluded ? 'Included as custom field' : 'Include as custom field'}>
                            <Box
                              style={{
                                width: 26,
                                height: 26,
                                minWidth: 26,
                                borderRadius: '50%',
                                background: isIncluded ? '#7C3AED' : 'transparent',
                                border: isIncluded ? 'none' : '1.5px solid #D1D5DB',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                              }}
                              onClick={() => onToggleCustom(mapping.csvColumn)}
                            >
                              {isIncluded ? (
                                <Check size={12} color="white" strokeWidth={2.5} />
                              ) : (
                                <Plus size={12} color="#9CA3AF" strokeWidth={2} />
                              )}
                            </Box>
                          </Tooltip>
                        ) : (
                          // Mapped - show checkmark colored by field type
                          <Box
                            style={{
                              width: 26,
                              height: 26,
                              minWidth: 26,
                              borderRadius: '50%',
                              background: fieldType === 'custom' ? '#7C3AED' : '#4A7C59',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Check size={12} color="white" strokeWidth={2.5} />
                          </Box>
                        )}
                        <Stack gap={2}>
                          <Badge
                            variant="default"
                            size="sm"
                            radius="sm"
                            styles={{
                              root: {
                                backgroundColor: '#F9F8F6',
                                border: '1px solid #E5E3DF',
                                color: '#1F2937',
                                fontWeight: 400,
                                textTransform: 'none',
                                fontSize: '12px',
                                padding: '4px 10px',
                                height: 'auto',
                              },
                            }}
                          >
                            {mapping.csvColumn}
                          </Badge>
                          {sampleValues && (
                            <Text size="xs" c="dimmed" lineClamp={1} style={{ maxWidth: 180 }}>
                              {sampleValues}
                            </Text>
                          )}
                        </Stack>
                      </Group>
                    </Table.Td>

                    {/* Arrow + Maps To Dropdown */}
                    <Table.Td style={{ padding: '16px', verticalAlign: 'middle' }}>
                      <Group gap="md" wrap="nowrap">
                        <Text c="dimmed" size="sm">&rarr;</Text>
                        <Select
                          placeholder={mapping.csvColumn}
                          data={[
                            { group: 'Required', items: STANDARD_FIELDS.filter((f) => f.type === 'required').map((f) => ({ value: f.value, label: f.label })) },
                            { group: 'Optional', items: STANDARD_FIELDS.filter((f) => f.type === 'optional').map((f) => ({ value: f.value, label: f.label })) },
                            { group: 'Custom', items: STANDARD_FIELDS.filter((f) => f.type === 'custom').map((f) => ({ value: f.value, label: f.label })) },
                          ]}
                          value={mapping.targetField || null}
                          onChange={(value) => onUpdateMapping(mapping.csvColumn, value)}
                          clearable={false}
                          allowDeselect={false}
                          w={180}
                          radius="md"
                          styles={{
                            input: mapping.targetField
                              ? fieldType === 'custom'
                                ? {
                                    backgroundColor: '#F5F3FF',
                                    color: '#1F2937',
                                    borderColor: '#7C3AED',
                                    borderWidth: '2px',
                                  }
                                : {
                                    backgroundColor: '#F0FDF4',
                                    color: '#1F2937',
                                    borderColor: '#4A7C59',
                                    borderWidth: '2px',
                                  }
                              : {
                                  backgroundColor: 'white',
                                  color: '#9CA3AF',
                                  borderColor: '#D1D5DB',
                                },
                          }}
                        />
                      </Group>
                    </Table.Td>

                    {/* Type Badge */}
                    <Table.Td style={{ padding: '16px', verticalAlign: 'middle' }}>
                      {fieldType === 'required' && (
                        <Badge
                          size="xs"
                          radius="xl"
                          styles={{
                            root: {
                              backgroundColor: '#FEE2E2',
                              color: '#B91C1C',
                              fontWeight: 500,
                              fontSize: '11px',
                              letterSpacing: '0.3px',
                              textTransform: 'uppercase',
                              border: 'none',
                              padding: '4px 12px',
                            },
                          }}
                        >
                          REQUIRED
                        </Badge>
                      )}
                      {fieldType === 'optional' && (
                        <Badge
                          size="xs"
                          radius="xl"
                          styles={{
                            root: {
                              backgroundColor: '#F3F4F6',
                              color: '#6B7280',
                              fontWeight: 500,
                              fontSize: '11px',
                              letterSpacing: '0.3px',
                              textTransform: 'uppercase',
                              border: 'none',
                              padding: '4px 12px',
                            },
                          }}
                        >
                          OPTIONAL
                        </Badge>
                      )}
                      {fieldType === 'custom' && (
                        <Badge
                          size="xs"
                          radius="xl"
                          styles={{
                            root: {
                              backgroundColor: '#EDE9FE',
                              color: '#7C3AED',
                              fontWeight: 500,
                              fontSize: '11px',
                              letterSpacing: '0.3px',
                              textTransform: 'uppercase',
                              border: 'none',
                              padding: '4px 12px',
                            },
                          }}
                        >
                          CUSTOM
                        </Badge>
                      )}
                    </Table.Td>

                    {/* Delete Action */}
                    <Table.Td style={{ padding: '16px', verticalAlign: 'middle' }}>
                      <Tooltip label={fieldType === 'required' ? 'Required field cannot be removed' : 'Remove column'}>
                        <Box
                          style={{
                            color: fieldType === 'required' ? '#D1D5DB' : '#9CA3AF',
                            cursor: fieldType === 'required' ? 'not-allowed' : 'pointer',
                          }}
                          onClick={() => fieldType !== 'required' && onIgnoreColumn(mapping.csvColumn)}
                        >
                          <Trash2 size={16} />
                        </Box>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </ScrollArea.Autosize>
      </Card>

      {/* Summary Footer */}
      <Card p="md" style={{ backgroundColor: '#F9FAFB', borderRadius: 8 }}>
        <Group gap="xl">
          <Group gap="xs">
            <Box style={{ width: 8, height: 8, borderRadius: '50%', background: '#4A7C59', flexShrink: 0 }} />
            <Text size="sm"><Text span fw={600}>{mappingSummary.standardMapped}</Text> mapped to standard fields</Text>
          </Group>
          <Group gap="xs">
            <Box style={{ width: 8, height: 8, borderRadius: '50%', background: '#7C3AED', flexShrink: 0 }} />
            <Text size="sm"><Text span fw={600}>{mappingSummary.customIncluded}</Text> custom fields</Text>
          </Group>
        </Group>
      </Card>
    </Stack>
  );
}
